/**
 * Phase 286 wave 7 — the reporting sub-nav strip, shared by BOTH reporting screens.
 *
 * Extracted verbatim (markup + styles) from the inline block that used to live only in
 * ForecastingScreen.tsx. That asymmetry was the 2026-08-17 Sign-Off bounce finding 1:
 * landing on /reporting/weekly-activity showed no strip at all, so there was no way back
 * to Forecasting except typing the URL. One component mounted by both screens is the
 * root-cause fix — the tab list can no longer drift between the two pages.
 *
 * Scope: a console-local component, deliberately NOT a @ardept/design-system primitive.
 * The portal-grade navigation redesign is carded separately (#1160 client reporting
 * portal / #1163 portal report views); this strip stays exactly as rich as it was.
 *
 * Navigation uses react-router's useNavigate internally — the same hook both screens
 * already hold for the NavRail — so a caller passes only which tab is current.
 *
 * Design tokens only (ReportingTabs.css) — hex-fence.test.ts POLISH-01.
 */
import { useNavigate } from "react-router-dom";
import "./ReportingTabs.css";

/** Identifier for one reporting screen — the `active` discriminator callers pass. */
export type ReportingTabId = "weekly-activity" | "forecasting";

interface ReportingTabSpec {
  id: ReportingTabId;
  label: string;
  /** Route path, mirroring the <Route> declarations in App.tsx. */
  path: string;
}

/**
 * The reporting section's tab list — the single source of truth for order, labels and
 * routes. Both screens render from this array, so adding a reporting screen is one entry
 * here rather than an edit in every screen.
 */
const REPORTING_TABS: readonly ReportingTabSpec[] = [
  { id: "weekly-activity", label: "Weekly Activity", path: "/reporting/weekly-activity" },
  { id: "forecasting", label: "Forecasting", path: "/reporting/forecasting" },
];

export interface ReportingTabsProps {
  /** Which reporting screen is currently rendered — gets `is-active` + `aria-current`. */
  active: ReportingTabId;
}

/**
 * Renders the reporting sub-nav. The current tab carries both the visual `is-active`
 * class and `aria-current="page"` (the two must stay paired — screen-reader parity with
 * the visual highlight).
 */
export function ReportingTabs({ active }: ReportingTabsProps): JSX.Element {
  const navigate = useNavigate();

  return (
    <nav className="rep-nav" aria-label="Reporting sections">
      {REPORTING_TABS.map((tab) => {
        const isActive = tab.id === active;
        return (
          <button
            key={tab.id}
            type="button"
            className={isActive ? "rep-nav__tab is-active" : "rep-nav__tab"}
            aria-current={isActive ? "page" : undefined}
            onClick={() => navigate(tab.path)}
          >
            {tab.label}
          </button>
        );
      })}
    </nav>
  );
}

export default ReportingTabs;
